// Energetic Alignment — scoring (EA1, plan §2). Pure + deterministic.
//
// The KEY idea: "centered flow" needs BOTH centricity near 4 AND internal cohesion.
// A domain can average 4 yet be a mix of very high and very low ([1,1,1,7,7,7,4]) —
// that is hidden inner conflict, NOT balance. Alignment = Centeredness × Cohesion
// catches it (Cohesion ≈ 0 there → Alignment ≈ 0). Alignment drives the core's
// vibrancy and the result type.

// First-pass thresholds (tunable — plan §9).
export const ENERGETIC_THRESHOLDS = {
  IDEAL: 4,
  SD_MAX: 3, // ScatterNorm = min(SD / SD_MAX, 1); max sample SD for a 1–7 split ≈ 3
  CENTERED_DEV: 0.5, // |Centricity − 4| ≤ this → "centered" (alignment type)
  TIGHT_PCT: 50, // ScatterPct < this → "tight" (alignment type)
  TRULY_BALANCED_DEV: 0.5,
  TRULY_BALANCED_SCATTER: 0.25, // ScatterNorm ≤ this
  STATE_LOW: 3.5, // Centricity < → Deficient
  STATE_HIGH: 4.5, // Centricity > → Excessive
  BAND_TIGHT: 33, // ScatterPct < → Tight
  BAND_WIDE: 66, // ScatterPct > → Wide
} as const;

export type EnergeticState = 'Deficient' | 'Centered' | 'Excessive';
export type ScatterBand = 'Tight' | 'Moderate' | 'Wide';
export type AlignmentType = 'Aligned' | 'Conflicted' | 'Fixed' | 'Scattered';

/** One answered reflection, carried in the payload so the results view can show
 *  the per-parameter over/under list (mirrors Life Balance's `matrix`). */
export interface DomainParam {
  parameter: string;
  answer: number; // 1–7
  deviation: number; // answer − 4 (signed; + = overactive)
}

export interface DomainScore {
  domainId: string;
  centricity: number; // 1–7, where energy centers (mean)
  range: number; // 0–6, Max − Min (secondary display stat)
  sd: number; // sample SD
  scatterNorm: number; // 0–1
  scatterPct: number; // 0–100, HOW spread / fragmented
  deviation: number; // −3 … +3 (signed; + = overactive)
  centeredness: number; // 0–1, how close the center is to 4
  cohesion: number; // 0–1, how consistent across the 7 params
  alignment: number; // 0–100, TRUE balance = centeredness × cohesion
  trulyBalanced: boolean;
  state: EnergeticState; // position only
  scatterBand: ScatterBand; // cohesion band
  alignmentType: AlignmentType; // the 2×2 headline read
  balancedCount: number; // # of ratings === 4
  params: DomainParam[]; // the 7 answered reflections (over/under list)
}

export interface EnergeticOverall {
  overallCentricity: number;
  overallCohesion: number; // within-domain: mean of the 7 cohesions
  systemScatterNorm: number; // across-domain SD of centricities, normalized
  systemScatterPct: number; // 0–100 (drives the holistic core size)
  overallAlignment: number; // 0–100
  overallState: EnergeticState;
  overallAlignmentType: AlignmentType;
}

export interface EnergeticMap {
  domains: DomainScore[];
  overall: EnergeticOverall;
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
const round1 = (n: number) => Math.round(n * 10) / 10;
const round2 = (n: number) => Math.round(n * 100) / 100;

function mean(xs: number[]): number {
  return xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : 0;
}

/** Sample standard deviation (n−1). Max ≈ 3 for a balanced 1–7 split. */
function sampleSd(xs: number[]): number {
  if (xs.length < 2) return 0;
  const m = mean(xs);
  const variance = xs.reduce((s, x) => s + (x - m) ** 2, 0) / (xs.length - 1);
  return Math.sqrt(variance);
}

export function stateFor(centricity: number): EnergeticState {
  if (centricity < ENERGETIC_THRESHOLDS.STATE_LOW) return 'Deficient';
  if (centricity > ENERGETIC_THRESHOLDS.STATE_HIGH) return 'Excessive';
  return 'Centered';
}

export function scatterBandFor(scatterPct: number): ScatterBand {
  if (scatterPct < ENERGETIC_THRESHOLDS.BAND_TIGHT) return 'Tight';
  if (scatterPct > ENERGETIC_THRESHOLDS.BAND_WIDE) return 'Wide';
  return 'Moderate';
}

/** The 2×2: centered/off-center × tight/wide. */
export function alignmentTypeFor(centricity: number, scatterPct: number): AlignmentType {
  const centered = Math.abs(centricity - ENERGETIC_THRESHOLDS.IDEAL) <= ENERGETIC_THRESHOLDS.CENTERED_DEV;
  const tight = scatterPct < ENERGETIC_THRESHOLDS.TIGHT_PCT;
  if (centered) return tight ? 'Aligned' : 'Conflicted';
  return tight ? 'Fixed' : 'Scattered';
}

/** Score one domain from its 7 ratings (each 1–7). `parameters` (parallel to
 *  `ratings`) is optional and only labels the per-parameter over/under list. */
export function scoreDomain(domainId: string, ratings: number[], parameters?: string[]): DomainScore {
  const { IDEAL, SD_MAX, TRULY_BALANCED_DEV, TRULY_BALANCED_SCATTER } = ENERGETIC_THRESHOLDS;
  const centricity = mean(ratings);
  const min = ratings.length ? Math.min(...ratings) : 0;
  const max = ratings.length ? Math.max(...ratings) : 0;
  const sd = sampleSd(ratings);
  const scatterNorm = clamp(sd / SD_MAX, 0, 1);
  const scatterPct = Math.round(scatterNorm * 100);
  const deviation = centricity - IDEAL;
  const centeredness = clamp(1 - Math.abs(deviation) / 3, 0, 1);
  const cohesion = 1 - scatterNorm;
  const alignment = Math.round(centeredness * cohesion * 100);
  const trulyBalanced = Math.abs(deviation) <= TRULY_BALANCED_DEV && scatterNorm <= TRULY_BALANCED_SCATTER;

  return {
    domainId,
    centricity: round2(centricity),
    range: max - min,
    sd: round2(sd),
    scatterNorm: round2(scatterNorm),
    scatterPct,
    deviation: round2(deviation),
    centeredness: round2(centeredness),
    cohesion: round2(cohesion),
    alignment,
    trulyBalanced,
    state: stateFor(centricity),
    scatterBand: scatterBandFor(scatterPct),
    alignmentType: alignmentTypeFor(centricity, scatterPct),
    balancedCount: ratings.filter(r => r === IDEAL).length,
    params: ratings.map((answer, i) => ({
      parameter: parameters?.[i] ?? `Parameter ${i + 1}`,
      answer,
      deviation: round2(answer - IDEAL),
    })),
  };
}

/** Build the overall (holistic) read from the per-domain scores. */
export function buildOverall(domains: DomainScore[]): EnergeticOverall {
  const { IDEAL, SD_MAX } = ENERGETIC_THRESHOLDS;
  const centricities = domains.map(d => d.centricity);
  const overallCentricity = mean(centricities);
  const overallCohesion = mean(domains.map(d => d.cohesion));
  // System scatter = SD ACROSS the 7 domain centricities (how even the whole is).
  const systemSd = sampleSd(centricities);
  const systemScatterNorm = clamp(systemSd / SD_MAX, 0, 1);
  const systemScatterPct = Math.round(systemScatterNorm * 100);
  const overallCenteredness = clamp(1 - Math.abs(overallCentricity - IDEAL) / 3, 0, 1);
  const overallAlignment = Math.round(overallCenteredness * overallCohesion * 100);
  // Overall type keys off within-domain cohesion (Conflicted = centered but
  // internally scattered on average — the hidden inner conflict).
  const overallAlignmentType = alignmentTypeFor(overallCentricity, (1 - overallCohesion) * 100);

  return {
    overallCentricity: round2(overallCentricity),
    overallCohesion: round2(overallCohesion),
    systemScatterNorm: round2(systemScatterNorm),
    systemScatterPct,
    overallAlignment,
    overallState: stateFor(overallCentricity),
    overallAlignmentType,
  };
}

/** Build the full map from a set of per-domain scores (one or all 7). */
export function buildEnergeticMap(domains: DomainScore[]): EnergeticMap {
  return { domains, overall: buildOverall(domains) };
}

/** A single answered cell (from the quiz). */
export interface EnergeticCell {
  domainId: string;
  parameter: string;
  answer: number; // 1–7
}

/** Group answered cells by domain → per-domain scores → full map. */
export function buildEnergeticResult(cells: EnergeticCell[]): EnergeticMap {
  const byDomain = new Map<string, EnergeticCell[]>();
  for (const c of cells) {
    if (!byDomain.has(c.domainId)) byDomain.set(c.domainId, []);
    byDomain.get(c.domainId)!.push(c);
  }
  const domains = Array.from(byDomain.entries()).map(([id, list]) =>
    scoreDomain(id, list.map(c => c.answer), list.map(c => c.parameter)),
  );
  return buildEnergeticMap(domains);
}

// ── Modular overview merge (latest take of each domain → full map) ───────────
export interface DomainMeta {
  completedAt: string;
  alignmentType: AlignmentType;
  state: EnergeticState;
  centricity: number;
  alignment: number;
}

export interface EnergeticOverview {
  result: EnergeticMap | null;
  perDomain: Record<string, DomainMeta>;
}

export function mergeLatestDomainResults(
  attempts: Array<{ completedAt: string; result: EnergeticMap | null | undefined }>,
  domainOrder: string[],
): EnergeticOverview {
  const sorted = attempts
    .filter(a => a.result && a.completedAt)
    .slice()
    .sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());

  const domains: DomainScore[] = [];
  const perDomain: Record<string, DomainMeta> = {};

  for (const id of domainOrder) {
    const found = sorted.find(a => a.result!.domains.some(d => d.domainId === id));
    if (!found) continue;
    const ds = found.result!.domains.find(d => d.domainId === id)!;
    perDomain[id] = {
      completedAt: found.completedAt,
      alignmentType: ds.alignmentType,
      state: ds.state,
      centricity: ds.centricity,
      alignment: ds.alignment,
    };
    domains.push(ds);
  }

  return { result: domains.length ? buildEnergeticMap(domains) : null, perDomain };
}
